import React, { Suspense, lazy, useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams, Link } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { SEOProvider } from './components/SEO';
import { FeatureFlagsProvider } from './contexts/FeatureFlagsContext';
import { TeamProvider } from './contexts/TeamContext';
import Navigation from './components/Navigation';
import AppShell from './components/AppShell';
import { useAuth } from './hooks/useAuth';
import { courseAPI } from './utils/api';
import './App.css';
import './styles/premium-theme.css';
import './components/ui/PageTransition.css';
import TrialBanner from './components/TrialBanner';
import SubscriptionGate from './components/SubscriptionGate';
import CoHostStudioGate from './components/CoHostStudioGate';
import FeatureGate from './components/FeatureGate';
import { initAffiliateTracking } from './utils/affiliateTracking';
import { initPostHog } from './utils/posthog';
import { initSitePixels, applyConsentToPixels, initSiteFacebookPixel, initGoogleAnalytics, initSiteOpenAIPixel } from './utils/pixels';
import { setCloudflareCustomerCode } from './utils/cloudflareThumb';
import { setXPostingEnabled } from './utils/xPosting';
import AISupportWidget from './components/AISupportWidget';
import ConnectionStatus from './components/common/ConnectionStatus';
import { ToastProvider, useToast } from './components/ui/Toast';
import { PROTECTED_ADMIN_EMAILS } from './utils/adminAccess';
import AdminCommandPalette from './components/admin/AdminCommandPalette';
import TierNotice from './components/TierNotice';
import ErrorBoundary from './components/ErrorBoundary';
import ApiErrorNotifier from './components/ApiErrorNotifier';
import MaintenanceBanner from './components/MaintenanceBanner';
import JobDoneNotifier from './components/JobDoneNotifier';
import ServerOutageGuard from './components/ServerOutageGuard';
import SkipLink from './components/common/SkipLink';
import CookieConsent, { getCookieConsent } from './components/CookieConsent';
import { purgeLegacyPrefs } from './utils/userPrefs';
import { TrialSignupProvider } from './contexts/TrialSignupContext';

// Delete preference values written before these keys were scoped to an account.
// They carry whichever user last used this browser, and there is no way to tell
// who that was — adopting one would hand the next person to sign in a stranger's
// setup, which is the bug being fixed. Runs once at startup, before any page
// reads a preference. Public-surface keys are deliberately not in the list; see
// LEGACY_UNSCOPED_KEYS.
purgeLegacyPrefs();

// Lazy load error pages
const NotFound = lazy(() => import('./pages/NotFound'));
const Maintenance = lazy(() => import('./pages/Maintenance'));

// Lazy load page components for better performance
const Home = lazy(() => import('./pages/HomePremium'));
const Login = lazy(() => import('./pages/LoginPremium'));
const CoHostInviteAccept = lazy(() => import('./pages/CoHostInviteAccept'));
const FollowHost = lazy(() => import('./pages/FollowHost'));
const TwoFactorChallengePage = lazy(() => import('./pages/TwoFactorChallengePage'));
const Register = lazy(() => import('./pages/RegisterPremium'));
const ForgotPassword = lazy(() => import('./pages/ForgotPasswordPremium'));
const ResetPassword = lazy(() => import('./pages/ResetPasswordPremium'));
const Features = lazy(() => import('./pages/FeaturesPremium'));
const TimeSavingsCalculator = lazy(() => import('./pages/TimeSavingsCalculator'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResendVerification = lazy(() => import('./pages/ResendVerification'));
const WebinarList = lazy(() => import('./pages/WebinarList'));
const WebinarDetails = lazy(() => import('./pages/WebinarDetails'));
const WebinarSession = lazy(() => import('./pages/WebinarSession'));
const WebinarManagement = lazy(() => import('./pages/WebinarManagement'));
const WebinarCreate = lazy(() => import('./pages/WebinarCreateSimple'));
const CheckoutWelcome = lazy(() => import('./pages/CheckoutWelcome'));
const FoundingWelcome = lazy(() => import('./pages/FoundingWelcome'));
const WebinarEdit = lazy(() => import('./pages/WebinarEdit'));
const WebinarAnalytics = lazy(() => import('./pages/WebinarAnalytics'));
const AIUsageAdmin = lazy(() => import('./pages/AIUsageAdmin'));
const ButtonDemo = lazy(() => import('./pages/ButtonDemo'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const HelpArticle = lazy(() => import('./pages/HelpArticle'));
const StreamingSettings = lazy(() => import('./pages/StreamingSettings'));
const Dashboard = lazy(() => import('./pages/DashboardHome'));
// Hub landing pages — introduced to collapse the sidebar from ~25 items to 5.
// Each hub is a purely additive card-grid landing page that links to existing
// routes; the underlying target pages are untouched and still directly
// accessible by URL, so bookmarks and in-app navigation continue to work.
const GrowHub = lazy(() => import('./pages/GrowHub'));
const SettingsHub = lazy(() => import('./pages/SettingsHub'));
const AdminHub = lazy(() => import('./pages/AdminHub'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Library = lazy(() => import('./pages/Library'));
const Destinations = lazy(() => import('./pages/Destinations'));
const StudioSetup = lazy(() => import('./pages/StudioSetup'));
// StudioEntry is the mount seam: it picks the phone-first MobileStudio (behind
// the mobile_studio_v2 flag, mobile viewport only) or the existing desktop
// BroadcastStudio. Desktop behaviour is unchanged. See pages/StudioEntry.js.
const StudioEntry = lazy(() => import('./pages/StudioEntry'));
// Lesson recording mode (/studio/lesson/:courseId/:lessonId): record one course
// lesson with its script on the teleprompter. Local recording only, never
// live; separate from StudioEntry so the live studio is untouched.
const LessonRecorder = lazy(() => import('./pages/LessonRecorder'));
// Full-studio lesson mode (/studio/lesson-full/:courseId/:lessonId): the desktop
// BroadcastStudio in record-only lessonMode. Only this route passes lessonMode;
// the live /studio/:sessionId route (StudioEntry) never does.
const LessonStudio = lazy(() => import('./pages/LessonStudio'));
const VideoAudioSetup = lazy(() => import('./pages/VideoAudioSetup'));
const SuperAdminDashboard = lazy(() => import('./pages/SuperAdminDashboard'));
const CaniraUniversity = lazy(() => import('./pages/CaniraUniversity'));
const MyAccount = lazy(() => import('./pages/MyAccount'));
const Members = lazy(() => import('./pages/Members'));
const TeamSettings = lazy(() => import('./pages/TeamSettings'));
const SystemStatus = lazy(() => import('./pages/SystemStatus'));
const PageBuilderPage = lazy(() => import('./pages/PageBuilderPage'));
const PublicWebinarPage = lazy(() => import('./pages/PublicWebinarPage'));
const EvergreenSettingsPage = lazy(() => import('./pages/EvergreenSettingsPage'));
const ContentCalendar = lazy(() => import('./pages/ContentCalendar'));
const AskCanira = lazy(() => import('./pages/AskCanira'));
const YourWeek = lazy(() => import('./pages/YourWeek'));
const YourWeeks = lazy(() => import('./pages/YourWeeks'));
const Premieres = lazy(() => import('./pages/Premieres'));
const BroadcastsDashboard = lazy(() => import('./pages/BroadcastsDashboard'));
const GuestJoin = lazy(() => import('./pages/GuestJoin'));
const Backstage = lazy(() => import('./pages/Backstage'));
const Stage = lazy(() => import('./pages/Stage'));
const WebinarWatch = lazy(() => import('./pages/WebinarWatch'));
const LiveWebinarWatch = lazy(() => import('./pages/LiveWebinarWatch'));
const WebinarReplay = lazy(() => import('./pages/WebinarReplay'));
const TemplateChooser = lazy(() => import('./pages/TemplateChooser'));
const TemplatePreview = lazy(() => import('./pages/TemplatePreview'));
const Cohort1Enrollment = lazy(() => import('./pages/Cohort1Enrollment'));
const TemplateLibrary = lazy(() => import('./pages/TemplateLibrary'));
const AddToCalendar = lazy(() => import('./pages/AddToCalendar'));
const WebinarRegisterSlug = lazy(() => import('./pages/WebinarRegisterSlug'));
const ClipLanding = lazy(() => import('./pages/ClipLanding'));
const WebinarPurchaseSuccess = lazy(() => import('./pages/WebinarPurchaseSuccess'));
const QRCodes = lazy(() => import('./pages/QRCodes'));
const PromoHubPage = lazy(() => import('./pages/PromoHubPage'));
const VideoLibrary = lazy(() => import('./pages/VideoLibrary'));
const MediaLibrary = lazy(() => import('./pages/MediaLibrary'));
const WebinarHub = lazy(() => import('./pages/WebinarHub'));
const Pricing = lazy(() => import('./pages/PricingPremium'));
const Founding25 = lazy(() => import('./pages/Founding25'));
const BookDemo = lazy(() => import('./pages/BookDemo'));
const GetApp = lazy(() => import('./pages/GetApp'));
const ManageBooking = lazy(() => import('./pages/ManageBooking'));
const BetaLanding = lazy(() => import('./pages/BetaLanding'));
const Workshop = lazy(() => import('./pages/Workshop'));
const WorkshopThankYou = lazy(() => import('./pages/WorkshopThankYou'));
const CoachWorkshop = lazy(() => import('./pages/CoachWorkshop'));
const CoachWorkshopThankYou = lazy(() => import('./pages/CoachWorkshopThankYou'));
const CoachWorkshopOffer = lazy(() => import('./pages/CoachWorkshopOffer'));
const BlogIndex = lazy(() => import('./pages/blog/BlogIndex'));
const BlogPost = lazy(() => import('./pages/blog/BlogPost'));
const AboutFounder = lazy(() => import('./pages/AboutFounder'));
const CouponManagement = lazy(() => import('./pages/CouponManagement'));
const DeveloperDashboard = lazy(() => import('./pages/DeveloperDashboard'));
const MonitoringDashboard = lazy(() => import('./pages/MonitoringDashboard'));
const PostHogAnalytics = lazy(() => import('./pages/PostHogAnalytics'));
const AdminTrackingSettings = lazy(() => import('./pages/AdminTrackingSettings'));
const AdminHomeTraining = lazy(() => import('./pages/AdminHomeTraining'));
const AdminAppStats = lazy(() => import('./pages/AdminAppStats'));
const AdminCustomerCosts = lazy(() => import('./pages/AdminCustomerCosts'));
const ComplianceDashboard = lazy(() => import('./pages/ComplianceDashboard'));
const SupportCenter = lazy(() => import('./pages/SupportCenter'));
const AffiliateDashboard = lazy(() => import('./pages/AffiliateDashboard'));
const CustomDomains = lazy(() => import('./pages/CustomDomains'));
const AffiliateAdmin = lazy(() => import('./pages/AffiliateAdmin'));
const AdminBookingSettings = lazy(() => import('./pages/AdminBookingSettings'));
const AdminSponsors = lazy(() => import('./pages/AdminSponsors'));
const AdminCaniraTVVideos = lazy(() => import('./pages/AdminCaniraTVVideos'));
const AdminTestSms = lazy(() => import('./pages/AdminTestSms'));
const AdminPlatformAnnounce = lazy(() => import('./pages/AdminPlatformAnnounce'));
const AdminWeeklyEmail = lazy(() => import('./pages/AdminWeeklyEmail'));
const AdminStorageCleanup = lazy(() => import('./pages/AdminStorageCleanup'));
const AdminDeploy = lazy(() => import('./pages/AdminDeploy'));
const AdminBillingStatus = lazy(() => import('./pages/AdminBillingStatus'));
const AdminSocialStatus = lazy(() => import('./pages/AdminSocialStatus'));
const AdminAutonomousHostTest = lazy(() => import('./pages/AdminAutonomousHostTest'));
const AdminTickerItems = lazy(() => import('./pages/AdminTickerItems'));
const AdminRokuChannels = lazy(() => import('./pages/AdminRokuChannels'));
const YourTVChannel = lazy(() => import('./pages/YourTVChannel'));
const TVChannelPreview = lazy(() => import('./pages/TVChannelPreview'));
const AdminCaniraTVHosts = lazy(() => import('./pages/AdminCaniraTVHosts'));
const AIClipGenerator = lazy(() => import('./pages/AIClipGenerator'));
const MyClips = lazy(() => import('./pages/MyClips'));
const ConnectedAccounts = lazy(() => import('./pages/ConnectedAccounts'));
const ConnectedApps = lazy(() => import('./pages/ConnectedApps'));
const OAuthAuthorize = lazy(() => import('./pages/OAuthAuthorize'));
const PaymentProcessors = lazy(() => import('./pages/PaymentProcessors'));
const AIScriptGenerator = lazy(() => import('./pages/AIScriptGenerator'));
const SlideDeckEditor = lazy(() => import('./pages/SlideDeckEditor'));
const BrandKit = lazy(() => import('./pages/BrandKit'));
const SlideDecks = lazy(() => import('./pages/SlideDecks'));
const AIThumbnailGenerator = lazy(() => import('./pages/AIThumbnailGenerator'));
const AIAgentSettings = lazy(() => import('./pages/AIAgentSettings'));
const AIAgentReport = lazy(() => import('./pages/AIAgentReport'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const SmsOptIn = lazy(() => import('./pages/SmsOptIn'));
const DataProcessingAgreement = lazy(() => import('./pages/DataProcessingAgreement'));
const DataDeletion = lazy(() => import('./pages/DataDeletion'));
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'));
const DMCAPolicy = lazy(() => import('./pages/DMCAPolicy'));
const TrustSecurity = lazy(() => import('./pages/TrustSecurity'));
const APIWebhooks = lazy(() => import('./pages/APIWebhooks'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const EmbedPlayer = lazy(() => import('./pages/EmbedPlayer'));
const TeamInvitePage = lazy(() => import('./pages/TeamInvitePage'));
const JoinByCode = lazy(() => import('./pages/JoinByCode'));

// Podcast pages
const PodcastsDashboard = lazy(() => import('./pages/PodcastsDashboard'));
const PodcastCreate = lazy(() => import('./pages/PodcastCreate'));
const PodcastDetail = lazy(() => import('./pages/PodcastDetail'));
const EpisodeCreate = lazy(() => import('./pages/EpisodeCreate'));
const EpisodeEdit = lazy(() => import('./pages/EpisodeEdit'));
const EpisodeAudioEditor = lazy(() => import('./pages/EpisodeAudioEditor'));
const PodcastSettings = lazy(() => import('./pages/PodcastSettings'));
const PodcastPublicPage = lazy(() => import('./pages/PodcastPublicPage'));
const PodcastBrowse = lazy(() => import('./pages/PodcastBrowse'));
const HostExplorePage = lazy(() => import('./pages/HostExplorePage'));

// Creator Courses pages
const CoursesDashboard = lazy(() => import('./pages/CoursesDashboard'));
const CourseCreate = lazy(() => import('./pages/CourseCreate'));
const CourseWizardPage = lazy(() => import('./pages/CourseWizardPage'));
const CourseBuilder = lazy(() => import('./pages/CourseBuilder'));
const CourseCatalog = lazy(() => import('./pages/CourseCatalog'));
const CreatorCoursePage = lazy(() => import('./pages/CreatorCoursePage'));
const CourseLearning = lazy(() => import('./pages/CourseLearning'));
// Course certificates: learner page, public verify, learner list
const CertificatePage = lazy(() => import('./pages/CertificatePage'));
const VerifyCertificate = lazy(() => import('./pages/VerifyCertificate'));
const MyCertificates = lazy(() => import('./pages/MyCertificates'));

// Feature pages (SEO-optimized landing pages)
const LiveStreamingFeature = lazy(() => import('./pages/features/LiveStreamingFeature'));
const EvergreenFeature = lazy(() => import('./pages/features/EvergreenFeature'));
const RegistrationFeature = lazy(() => import('./pages/features/RegistrationFeature'));
const AutomationFeature = lazy(() => import('./pages/features/AutomationFeature'));
const PodcastFeature = lazy(() => import('./pages/features/PodcastFeature'));
const RepurposingFeature = lazy(() => import('./pages/features/RepurposingFeature'));
const ForCoaches = lazy(() => import('./pages/ForCoaches'));
const SegmentPage = lazy(() => import('./components/segment/SegmentPage'));
const ComparePage = lazy(() => import('./components/compare/ComparePage'));
const CompareIndex = lazy(() => import('./components/compare/CompareIndex'));
const Monetization = lazy(() => import('./pages/Monetization'));
const Switching = lazy(() => import('./pages/Switching'));

// Admin pages
const FeatureFlags = lazy(() => import('./pages/FeatureFlags'));

// Protected route component for React Router 6.
//
// Two mutually exclusive gating flags:
//
//   requireSubscription — "user must have access" (admin, paid, trial, or free).
//     This is the legacy flag. After the fix-free-plan-access PR,
//     getSubscriptionStatus().hasAccess returns TRUE for free-plan users,
//     so `requireSubscription` no longer blocks free users — it only
//     blocks trial-expired users with `plan==='none'`. Safe on core
//     product routes (dashboard, webinars/create, broadcast studio)
//     where free users SHOULD have access.
//
//   requirePaidPlan — "user must have a paid plan OR a valid trial".
//     Free users are blocked even though they're fully authenticated.
//     Used for the Grow hub features (AI tools, Promo Hub, Landing
//     Pages, A/B Testing, QR Codes, Course Builder, etc.) that should
//     convert free users via an upgrade prompt when they click.
//     Renders <SubscriptionGate blockFreeUsers /> which forces the
//     upgrade card instead of short-circuiting on hasAccess=true.
// Protected admin emails — break-glass accounts that get admin
// treatment REGARDLESS of `user.role`. Mirrors backend
// `config.security.protectedAdminEmails` (env.js:183-186) +
// `adminAuth.js:26-28`. The whole point is to be the safety net
// when role data drifts (accidental demotion, corrupt session,
// migration mishap) — without this, the frontend gate would
// lock these accounts out even though the backend still serves
// them every admin API. Keep this list in lockstep with the
// backend list. A future hardening: expose `isProtectedAdmin` on
// the `/auth/me` payload so this isn't duplicated client-side.
// The list lives in utils/adminAccess.js so the admin jump-to box uses the same rule.

const PrivateRoute = ({
  children,
  requireSubscription = false,
  requirePaidPlan = false,
  // No free usage: every product route requires an active plan or a live trial
  // by default. Routes a plan-less user still needs (billing/account) opt out
  // with requirePlan={false}. Attendee/viewer routes are public (not
  // PrivateRoute), so this never blocks registering for or watching a webinar.
  requirePlan = true,
  // A lapsed customer (had a plan or trial, doesn't now) may still open this
  // route read-only — their registrants and webinars are their data. The
  // server refuses anything that needs a plan; the page shows why
  // (PlanStatusBanner). A never-subscribed user is still sent to /pricing.
  allowLapsed = false,
  // The studio: an invited co-host without a plan of their own is let in when
  // the server says the webinar HOST's plan covers them (CoHostStudioGate).
  allowStudioCoHost = false,
  adminOnly = false,
  feature = 'this feature',
}) => {
  const { currentUser, loading, getSubscriptionStatus } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admin check — matches backend middleware:
  //   1. role === 'admin' (regular admin)
  //   2. role === 'super_admin' (top-level admin)
  //   3. email is in the protected break-glass list
  // (1) and (2) match auth.js#isAdmin; (3) matches the
  // protectedAdminEmails safety net in adminAuth.js.
  const userEmail = (currentUser.email || '').toLowerCase();
  const isAdmin =
    currentUser.role === 'admin' ||
    currentUser.role === 'super_admin' ||
    PROTECTED_ADMIN_EMAILS.includes(userEmail);

  // adminOnly gate: non-admins trying to reach an admin route get
  // bounced to /dashboard. We don't show a 403 page — they may
  // have been pointed at the URL accidentally (a forwarded link,
  // a stale bookmark) and a silent redirect is friendlier than an
  // intrusive error. The backend still enforces admin checks at
  // every API endpoint regardless, so a curious user clicking
  // /admin can't actually DO anything privileged.
  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  // Admins always bypass every subscription / plan check — never block
  // an internal test or support session.
  if (isAdmin) {
    return children;
  }

  // Plan gate (no free usage): an authenticated non-admin with no active plan
  // and no live trial is sent to /pricing to choose one. This walls the whole
  // product — the free tier is no longer usable. Billing/account routes opt
  // out via requirePlan={false} so a plan-less user can still manage billing
  // and so the post-checkout /my-account redirect never loops.
  if (requirePlan) {
    // The rule lives in getSubscriptionStatus (AuthContext). A seat on a
    // Studio-or-above team counts: without it the wall bounces an invited
    // teammate to /pricing before the dashboard.
    const status = getSubscriptionStatus();
    if (!status.hasPlanAccess) {
      if (allowLapsed && status.lapsed) return children;
      const toPricing = (
        <Navigate to="/pricing" state={{ from: location, needPlan: true, lapsed: status.lapsed }} replace />
      );
      if (allowStudioCoHost) {
        return (
          <CoHostStudioGate fallback={toPricing} loading={<LoadingFallback />}>
            {children}
          </CoHostStudioGate>
        );
      }
      return toPricing;
    }
  }

  // Paid-plan gate: free users get bounced to the upgrade prompt even
  // though they're authenticated. Trial users pass (they have
  // Professional-tier features during trial).
  if (requirePaidPlan) {
    const status = getSubscriptionStatus();
    const needsUpgrade =
      !status.hasPaidPlan && !status.isTrialing && !status.isActiveSubscription;
    if (needsUpgrade) {
      return <SubscriptionGate feature={feature} blockFreeUsers />;
    }
  }

  // Legacy subscription gate: blocks trial-expired users with no plan.
  // Free users pass because hasAccess=true post fix-free-plan-access.
  if (requireSubscription) {
    const status = getSubscriptionStatus();
    if (!status.hasAccess) {
      return <SubscriptionGate feature={feature} />;
    }
  }

  return children;
};

// Gate for the private course builder (/courses/:courseId — plural). The
// PUBLIC, shareable page is the singular /course/:courseId; people routinely
// copy the plural editor URL out of their address bar and share that by
// mistake, which for the audience is a private page (login/upgrade wall) with
// no social-share card. Instead of bouncing non-owners to /login or the
// upgrade gate, resolve ownership and send anyone who isn't the owner to the
// public course page — so a mistakenly-shared editor link still lands the
// audience on the real landing page (and its OG card). Only the owner reaches
// the builder, still behind the paid-plan gate.
const CourseBuilderRoute = () => {
  const { courseId } = useParams();
  const { currentUser, loading: authLoading } = useAuth();
  const [ownership, setOwnership] = useState('checking'); // 'checking' | 'owner' | 'deny' | 'plan'

  useEffect(() => {
    if (authLoading) return undefined;
    if (!currentUser) {
      setOwnership('deny');
      return undefined;
    }
    let cancelled = false;
    // Owner-scoped fetch: 200 for the owner, 403/404 otherwise. This works for
    // DRAFT courses too — the public endpoint 404s on drafts, so it can't be
    // used to check ownership. suppressGlobalError: the 403 for a non-owner is
    // expected and handled by the redirect; don't flash a global error toast.
    courseAPI.getById(courseId, { suppressGlobalError: true })
      .then(() => { if (!cancelled) setOwnership('owner'); })
      .catch((err) => {
        if (cancelled) return;
        // The plan doesn't include courses (lapsed / downgraded): that's an
        // upgrade prompt for the host, not "you're not the owner" — which
        // bounced them to the public page and then the catalog.
        setOwnership(err?.response?.data?.code === 'FEATURE_NOT_AVAILABLE' ? 'plan' : 'deny');
      });
    return () => { cancelled = true; };
  }, [courseId, currentUser, authLoading]);

  if (authLoading || ownership === 'checking') {
    return <LoadingFallback />;
  }
  if (ownership === 'deny') {
    return <Navigate to={`/course/${courseId}`} replace />;
  }
  if (ownership === 'plan') {
    return <SubscriptionGate feature="course builder" blockFreeUsers />;
  }
  return (
    <PrivateRoute requirePaidPlan feature="course builder">
      <CourseBuilder />
    </PrivateRoute>
  );
};

// Loading component to show while page components are loading
// /webinars/:id/register was a second, older sign-up page for the same
// session (different fields; it asked for a job title nothing saved). The
// main page loads by id too, so old links land there, UTM tags and all.
const LegacyRegisterRedirect = () => {
  const { id } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/register/${id}${search}`} replace />;
};

const LoadingFallback = () => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <span className="loading-text">Loading</span>
    <div className="loading-dots">
      <span></span>
      <span></span>
      <span></span>
    </div>
  </div>
);

// Studio-specific error fallback — shows the actual error so we can debug mobile crashes
const StudioErrorFallback = ({ error, resetError }) => {
  // Log the error to the server so we can see it in backend logs
  React.useEffect(() => {
    if (error) {
      const errorData = {
        message: error?.message || String(error),
        stack: error?.stack || '',
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      };
      console.error('[StudioErrorFallback]', errorData);
      fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData)
      }).catch(() => {}); // ignore if endpoint doesn't exist yet
    }
  }, [error]);

  const errorMsg = error?.message || String(error || 'Unknown error');

  return (
    <div style={{ padding: '2rem', maxWidth: 600, margin: '4rem auto', fontFamily: 'system-ui, sans-serif', textAlign: 'center' }}>
      <h2 style={{ color: '#e53e3e', marginBottom: '1rem' }}>Studio Failed to Load</h2>
      <p style={{ color: '#666', marginBottom: '1rem' }}>
        The broadcast studio encountered an error on this device.
      </p>
      <div style={{ textAlign: 'left', background: '#fff3f3', padding: '1rem', borderRadius: 8, marginBottom: '1.5rem', border: '1px solid #e53e3e' }}>
        <strong style={{ fontSize: 14, color: '#c00' }}>Error:</strong>
        <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 14, marginTop: '0.5rem', color: '#c00' }}>
          {errorMsg}
        </p>
      </div>
      {error?.stack && (
        <details style={{ textAlign: 'left', background: '#f7f7f7', padding: '1rem', borderRadius: 8, marginBottom: '1.5rem' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Stack Trace</summary>
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, marginTop: '0.5rem', color: '#888' }}>
            {error.stack}
          </pre>
        </details>
      )}
      <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
        <button
          onClick={() => window.location.reload()}
          style={{ padding: '0.75rem 1.5rem', background: '#E0C98E', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15 }}
        >
          Retry
        </button>
        <button
          onClick={() => { if (resetError) resetError(); window.location.href = '/dashboard'; }}
          style={{ padding: '0.75rem 1.5rem', background: '#e2e8f0', color: '#333', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15 }}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

// Footer component that hides on premium pages
const Footer = () => {
  const location = useLocation();

  // Pages with premium navigation have their own footer
  const premiumPages = [
    '/',
    '/login',
    '/login/2fa',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/pricing',
    '/founding-25',
    '/time-savings',
    '/workshop',
    '/workshop/thank-you',
    '/coach-workshop',
    '/coach-workshop/thank-you',
    '/coach-workshop/offer',
    '/features',
    '/features/live-streaming',
    '/features/evergreen',
    '/features/registration',
    '/features/automation',
    '/features/podcasts'
  ];

  // Also hide footer on live webinar watch pages (full-screen viewer experience)
  const isLiveWebinarPage = location.pathname.match(/^\/webinar\/[^/]+\/live$/);

  // Also hide footer on evergreen webinar watch pages (full-screen viewer experience)
  const isEvergreenWatchPage = location.pathname.match(/^\/webinar\/[^/]+\/watch$/);

  // Branded clip landing + recording watch pages ship their own footer/chrome.
  const isClipPage = location.pathname.match(/^\/(clip|watch)\//);

  if (premiumPages.includes(location.pathname) || isLiveWebinarPage || isEvergreenWatchPage || isClipPage) {
    return null;
  }

  return (
    <footer className="app-footer">
      <p>&copy; {new Date().getFullYear()} Canira. All rights reserved.</p>
      <nav className="footer-legal-links" style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginTop: '8px', fontSize: '12px' }}>
        <Link to="/terms" style={{ color: '#888', textDecoration: 'none' }}>Terms</Link>
        <Link to="/privacy" style={{ color: '#888', textDecoration: 'none' }}>Privacy</Link>
        <Link to="/dmca" style={{ color: '#888', textDecoration: 'none' }}>DMCA</Link>
        <Link to="/trust" style={{ color: '#888', textDecoration: 'none' }}>Trust & Security</Link>
      </nav>
    </footer>
  );
};

// Scroll restoration component
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
};

// Feeds the current pathname to ThemeContext. ThemeProvider sits
// outside Router (so it survives route changes) and can't read
// useLocation itself; this component lives inside Router and does.
//
// The theme does NOT follow the route — it is sticky, and only the
// toggle changes it. syncToRoute uses the pathname for two things:
// pinning the auth routes to light, and the once-per-browser dark
// seed the first time a visitor reaches the workspace. See the header
// comment in ThemeContext.js.
// Canira's own Meta Pixel only loads on Canira's pages (utils/pixels.js
// isCaniraMarketingPage). A visitor who lands on a host's page and then
// browses to the marketing site gets it on that navigation.
const SitePixelRouteSync = () => {
  const { pathname } = useLocation();
  useEffect(() => { applyConsentToPixels(); }, [pathname]);
  return null;
};

const ThemeRouteSync = () => {
  const { pathname } = useLocation();
  const { syncToRoute } = useTheme();

  // useLayoutEffect (not useEffect) so the route lands before the
  // browser paints. It matters on the pinned auth routes: entering
  // /login from a dark session would otherwise paint dark once and
  // then flip to light.
  useLayoutEffect(() => {
    syncToRoute(pathname);
  }, [pathname, syncToRoute]);

  return null;
};

function AppContent() {
  const toast = useToast();

  // Analytics + ad pixels, gated on cookie consent.
  //   - PostHog + GA4 fire only with "analytics" consent; Meta Pixel with
  //     "marketing". Nothing fires until the visitor chooses.
  //   - initSitePixels() registers the env-configured pixels (they queue and
  //     fire once consent allows). applyConsent() runs on mount (so returning
  //     visitors who already chose are honored) and on every consent change.
  useEffect(() => {
    initSitePixels(); // env-configured pixels (build-time fallback)
    // Admin-configured pixels (site_settings, editable without a redeploy).
    // Registered the same way — consent-gated + deduped against the env ones.
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    fetch(`${apiBase}/site-config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cfg) return;
        if (cfg.fbPixelId) initSiteFacebookPixel(cfg.fbPixelId);
        if (cfg.gaMeasurementId) initGoogleAnalytics(cfg.gaMeasurementId);
        if (cfg.oaiPixelId) initSiteOpenAIPixel(cfg.oaiPixelId);
        if (cfg.cloudflareCustomerCode) setCloudflareCustomerCode(cfg.cloudflareCustomerCode);
        setXPostingEnabled(cfg.features?.xPosting === true);
        applyConsentToPixels();
      })
      .catch(() => { /* config endpoint optional — env still applies */ });

    const applyConsent = () => {
      if (getCookieConsent()?.analytics) initPostHog();
      applyConsentToPixels();
    };
    applyConsent();
    window.addEventListener('cookieConsentChanged', applyConsent);
    return () => window.removeEventListener('cookieConsentChanged', applyConsent);
  }, []);

  // Initialize affiliate tracking on app load
  useEffect(() => {
    initAffiliateTracking().then(referral => {
      if (referral) {
      }
    });
  }, []);

  // Show theme hint toast for first-time visitors. Not on the homepage: the
  // free-training card slides into the same corner there, and a first visit
  // is exactly when both would show. It isn't marked shown, so it still
  // appears on a later visit that opens another page. Not on the guest pages
  // (/join, /backstage, /stage) either: they hide the navigation bar, so the
  // toggle it points at isn't there, and it lands over a guest's camera check.
  useEffect(() => {
    if (window.location.pathname === '/') return undefined;
    if (/^\/(join|backstage|stage)\/[^/]+$/.test(window.location.pathname)) return undefined;
    const toastShown = localStorage.getItem('canira-theme-toast-shown');
    if (!toastShown) {
      const timer = setTimeout(() => {
        toast.info('You can switch between dark and light mode using the theme toggle in the navigation bar.', {
          title: 'Light or dark?',
          duration: 8000,
        });
        localStorage.setItem('canira-theme-toast-shown', '1');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Embedded mode: the native mobile Studio WebView loads /studio/:id?embedded=1.
  // In that shell we hide all site chrome (nav, footer, banners, floating widgets)
  // so the WebView shows only the Studio surface — no duplicate header/footer.
  // Sticky via sessionStorage so any in-app navigation that drops the query param
  // stays chrome-free for the rest of the session.
  const [isEmbedded] = useState(() => {
    if (typeof window === 'undefined') return false;
    let hasParam = false;
    try {
      hasParam = new URLSearchParams(window.location.search).has('embedded');
    } catch (_) {
      /* malformed query string — treat as no param */
    }
    // The native shell injects window.ReactNativeWebView before page load.
    const nativeShell = !!window.ReactNativeWebView;
    if (hasParam) {
      // Persist ONLY inside the native shell, so in-app navigation that drops
      // the query param stays chrome-free. On the open web we deliberately do
      // NOT persist — otherwise a stray ?embedded=1 link would strip site chrome
      // for the whole browser session across every route.
      if (nativeShell) {
        try {
          sessionStorage.setItem('canira.embedded', '1');
        } catch (_) {
          /* sessionStorage unavailable — param alone still drives this load */
        }
      }
      return true;
    }
    if (!nativeShell) return false;
    try {
      return sessionStorage.getItem('canira.embedded') === '1';
    } catch (_) {
      return false;
    }
  });

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <TrialSignupProvider>
      <ScrollToTop />
      <SitePixelRouteSync />
      <ThemeRouteSync />
      <div className={`App${isEmbedded ? ' app-embedded' : ''}`}>
        <SkipLink targetId="main-content" />
        {!isEmbedded && <Navigation />}
        {!isEmbedded && <TrialBanner />}
        {/* Persistent chrome — lives outside <Routes> so the sidebar survives
            navigation instead of remounting (and refetching) with each page. */}
        {!isEmbedded && <AppShell />}
        <main id="main-content" className="main-content animate-fade-in" role="main">
          <ErrorBoundary>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
              {/* Public routes */}
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/login/2fa" element={<TwoFactorChallengePage />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/founding-25" element={<Founding25 />} />
              <Route path="/demo" element={<BookDemo />} />
              <Route path="/app" element={<GetApp />} />
              <Route path="/book/manage/:rescheduleToken" element={<ManageBooking />} />
              <Route path="/beta" element={<BetaLanding />} />
              <Route path="/workshop" element={<Workshop />} />
              <Route path="/workshop/thank-you" element={<WorkshopThankYou />} />
              <Route path="/coach-workshop" element={<CoachWorkshop />} />
              <Route path="/coach-workshop/thank-you" element={<CoachWorkshopThankYou />} />
              <Route path="/coach-workshop/offer" element={<CoachWorkshopOffer />} />
              <Route path="/features" element={<Features />} />
              <Route path="/time-savings" element={<TimeSavingsCalculator />} />
              {/* No-login unsubscribe from a host's announcement emails */}
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/help" element={<HelpCenter />} />
              <Route path="/help/:slug" element={<HelpArticle />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/sms-opt-in" element={<SmsOptIn />} />
              <Route path="/data-processing" element={<DataProcessingAgreement />} />
              <Route path="/delete" element={<DataDeletion />} />
              <Route path="/data-deletion" element={<DataDeletion />} />
              {/* Mobile-app-focused account deletion instructions. Linked
                  from the Google Play Store listing's "Delete account URL"
                  field (required by Play policy for any app with user
                  accounts). Complements the form-based /data-deletion
                  page above with step-by-step in-app instructions. */}
              <Route path="/delete-account" element={<DeleteAccount />} />
              <Route path="/dmca" element={<DMCAPolicy />} />
              <Route path="/trust" element={<TrustSecurity />} />
              <Route path="/security" element={<TrustSecurity />} />

              {/* Feature pages (SEO-optimized) */}
              <Route path="/features/live-streaming" element={<LiveStreamingFeature />} />
              <Route path="/features/evergreen" element={<EvergreenFeature />} />
              <Route path="/features/registration" element={<RegistrationFeature />} />
              <Route path="/features/automation" element={<AutomationFeature />} />
              <Route path="/features/podcasts" element={<PodcastFeature />} />
              <Route path="/features/repurposing" element={<RepurposingFeature />} />
              {/* Short alias for ad traffic — the repurposing page is the
                  destination for the highest-intent campaigns, and a short URL
                  reads better in an ad than /features/repurposing. Same
                  component; the canonical URL in its <SEO> tag stays
                  /features/repurposing so the two do not compete in search. */}
              <Route path="/repurposing" element={<RepurposingFeature />} />
              <Route path="/for-coaches" element={<ForCoaches />} />
              <Route path="/for-churches" element={<SegmentPage segment="churches" />} />
              <Route path="/for-educators" element={<SegmentPage segment="educators" />} />
              <Route path="/for-speakers" element={<SegmentPage segment="speakers" />} />
              <Route path="/compare" element={<CompareIndex />} />
              <Route path="/compare/streamyard" element={<ComparePage slug="streamyard" />} />
              <Route path="/compare/everwebinar" element={<ComparePage slug="everwebinar" />} />
              <Route path="/compare/opus-clip" element={<ComparePage slug="opus-clip" />} />
              <Route path="/monetization" element={<Monetization />} />
              <Route path="/switching" element={<Switching />} />

              {/* Founder log / blog */}
              <Route path="/blog" element={<BlogIndex />} />
              <Route path="/blog/:slug" element={<BlogPost />} />

              {/* About / founder — /founder is an alias for /about */}
              <Route path="/about" element={<AboutFounder />} />
              <Route path="/founder" element={<AboutFounder />} />
              <Route path="/webinars" element={<WebinarList />} />
              <Route path="/webinars/:id" element={<WebinarDetails />} />
              <Route path="/explore/:hostId" element={<HostExplorePage />} />
              <Route path="/ui/buttons" element={<ButtonDemo />} />

              {/* Protected routes */}
              <Route
                path="/webinars/:webinarId/session/:sessionId"
                element={
                  <PrivateRoute>
                    <WebinarSession />
                  </PrivateRoute>
                }
              />
              <Route
                path="/webinars/manage"
                element={
                  <PrivateRoute>
                    <WebinarManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/webinars/create"
                element={
                  <PrivateRoute requireSubscription feature="creating webinars">
                    <WebinarCreate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/webinars/edit/:webinarId"
                element={
                  <PrivateRoute>
                    <WebinarEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/webinars/:webinarId/analytics"
                element={
                  <PrivateRoute>
                    <WebinarAnalytics />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/streaming"
                element={
                  <PrivateRoute>
                    <StreamingSettings />
                  </PrivateRoute>
                }
              />

              {/* Dashboard routes */}
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              {/* Hub landing pages — collapse the sidebar into three focused hubs */}
              <Route
                path="/grow"
                element={
                  <PrivateRoute>
                    <GrowHub />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <SettingsHub />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin-hub"
                element={
                  <PrivateRoute adminOnly>
                    <AdminHub />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/contacts"
                element={
                  <PrivateRoute allowLapsed>
                    <Contacts />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/ask"
                element={
                  <PrivateRoute>
                    <AskCanira />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/week"
                element={
                  <PrivateRoute>
                    <YourWeeks />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/week/:runId"
                element={
                  <PrivateRoute>
                    <YourWeek />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/calendar"
                element={
                  <PrivateRoute>
                    <ContentCalendar />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/premieres"
                element={
                  <PrivateRoute>
                    <TierNotice feature="Premieres"><Premieres /></TierNotice>
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/library"
                element={
                  <PrivateRoute>
                    <Library />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/webinars"
                element={
                  <PrivateRoute>
                    <Library />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/destinations"
                element={
                  <PrivateRoute>
                    <Destinations />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/members"
                element={
                  <PrivateRoute>
                    <Members />
                  </PrivateRoute>
                }
              />
              {/* Referrals was the affiliate program on a second page, with
                  different numbers (it counted unapproved commissions as
                  available). One page now. */}
              <Route path="/dashboard/referrals" element={<Navigate to="/affiliates" replace />} />
              <Route
                path="/dashboard/settings"
                element={
                  <PrivateRoute>
                    <TeamSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/status"
                element={
                  <PrivateRoute>
                    <SystemStatus />
                  </PrivateRoute>
                }
              />

              {/* Studio routes */}
              <Route
                path="/setup/:sessionId"
                element={
                  <PrivateRoute requireSubscription feature="broadcast setup">
                    <StudioSetup />
                  </PrivateRoute>
                }
              />
              <Route
                path="/studio/:sessionId"
                element={
                  <PrivateRoute requireSubscription allowStudioCoHost feature="live broadcasting">
                    <ErrorBoundary fallback={(error, resetError) => <StudioErrorFallback error={error} resetError={resetError} />}>
                      <StudioEntry />
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />
              {/* Lesson recording mode — same plan gate as the course builder. The
                  static "lesson" segment outranks /studio/:sessionId, which only
                  ever matches one segment anyway. */}
              <Route
                path="/studio/lesson/:courseId/:lessonId"
                element={
                  <PrivateRoute requirePaidPlan feature="course builder">
                    <ErrorBoundary fallback={(error, resetError) => <StudioErrorFallback error={error} resetError={resetError} />}>
                      <LessonRecorder />
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />
              {/* Full Broadcast Studio in record-only lesson mode — same plan gate
                  and error fallback as the lesson recorder. Three segments, so it
                  can never match /studio/:sessionId. */}
              <Route
                path="/studio/lesson-full/:courseId/:lessonId"
                element={
                  <PrivateRoute requirePaidPlan feature="course builder">
                    <ErrorBoundary fallback={(error, resetError) => <StudioErrorFallback error={error} resetError={resetError} />}>
                      <LessonStudio />
                    </ErrorBoundary>
                  </PrivateRoute>
                }
              />

              {/* Video & Audio Setup (Quick Start flow) */}
              <Route
                path="/setup"
                element={
                  <PrivateRoute>
                    <VideoAudioSetup />
                  </PrivateRoute>
                }
              />

              {/* Account route. Both /account and /my-account render MyAccount:
                  the Stripe checkout success_url, the billing-portal return URL,
                  and MyAccount's own history.replaceState all use /my-account,
                  so it must resolve or those flows 404 after payment. */}
              <Route
                path="/account"
                element={
                  <PrivateRoute requirePlan={false}>
                    <MyAccount />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-account"
                element={
                  <PrivateRoute requirePlan={false}>
                    <MyAccount />
                  </PrivateRoute>
                }
              />
              {/* Where Stripe returns after checkout: waits for the plan to
                  register, then "Your trial has started" + first session.
                  No plan wall — the plan may still be arriving. */}
              {/* Where the shareable founding link returns a buyer who had no
                  account: choose a password here, then /welcome. Public — they
                  aren't signed in yet (routes/auth.js /founding-claim). */}
              <Route path="/welcome/founding" element={<FoundingWelcome />} />
              <Route
                path="/welcome"
                element={
                  <PrivateRoute requirePlan={false}>
                    <CheckoutWelcome />
                  </PrivateRoute>
                }
              />

              {/* Affiliate Dashboard */}
              <Route
                path="/affiliates"
                element={
                  <PrivateRoute>
                    <AffiliateDashboard />
                  </PrivateRoute>
                }
              />

              {/* Canira University — admin-made training videos for users.
                  Open to lapsed accounts too (how-to and win-back content).
                  /university/:courseId/:lessonId deep-links a lesson. */}
              <Route
                path="/university/:courseId?/:lessonId?"
                element={
                  <PrivateRoute allowLapsed>
                    <CaniraUniversity />
                  </PrivateRoute>
                }
              />

              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <PrivateRoute adminOnly>
                    <SuperAdminDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/coupons"
                element={
                  <PrivateRoute adminOnly>
                    <CouponManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/affiliates"
                element={
                  <PrivateRoute adminOnly>
                    <AffiliateAdmin />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/booking-settings"
                element={
                  <PrivateRoute adminOnly>
                    <AdminBookingSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/sponsors"
                element={
                  <PrivateRoute adminOnly>
                    <AdminSponsors />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/canira-tv-videos"
                element={
                  <PrivateRoute adminOnly>
                    <AdminCaniraTVVideos />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/canira-tv/preview"
                element={
                  <PrivateRoute adminOnly>
                    <TVChannelPreview />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/sms-test"
                element={
                  <PrivateRoute adminOnly>
                    <AdminTestSms />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/storage-cleanup"
                element={
                  <PrivateRoute adminOnly>
                    <AdminStorageCleanup />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/platform-announce"
                element={
                  <PrivateRoute adminOnly>
                    <AdminPlatformAnnounce />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/weekly-email"
                element={
                  <PrivateRoute adminOnly>
                    <AdminWeeklyEmail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/deploy"
                element={
                  <PrivateRoute adminOnly>
                    <AdminDeploy />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/billing"
                element={
                  <PrivateRoute adminOnly>
                    <AdminBillingStatus />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/social-status"
                element={
                  <PrivateRoute adminOnly>
                    <AdminSocialStatus />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/autonomous-host-test"
                element={
                  <PrivateRoute adminOnly>
                    <AdminAutonomousHostTest />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/canira-tv-hosts"
                element={
                  <PrivateRoute adminOnly>
                    <AdminCaniraTVHosts />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/ticker-items"
                element={
                  <PrivateRoute adminOnly>
                    <AdminTickerItems />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/roku-channels"
                element={
                  <PrivateRoute adminOnly>
                    <AdminRokuChannels />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/tv-channel"
                element={
                  <PrivateRoute>
                    <YourTVChannel />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard/tv-channel/preview/:slug"
                element={
                  <PrivateRoute>
                    <TVChannelPreview />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/developer"
                element={
                  <PrivateRoute adminOnly>
                    <DeveloperDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/support"
                element={
                  <PrivateRoute adminOnly>
                    <SupportCenter />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/monitoring"
                element={
                  <PrivateRoute adminOnly>
                    <MonitoringDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/compliance"
                element={
                  <PrivateRoute adminOnly>
                    <ComplianceDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/features"
                element={
                  <PrivateRoute adminOnly>
                    <FeatureFlags />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <PrivateRoute adminOnly>
                    <PostHogAnalytics />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/tracking"
                element={
                  <PrivateRoute adminOnly>
                    <AdminTrackingSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/app-stats"
                element={
                  <PrivateRoute adminOnly>
                    <AdminAppStats />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/home-training"
                element={
                  <PrivateRoute adminOnly>
                    <AdminHomeTraining />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/customer-costs"
                element={
                  <PrivateRoute adminOnly>
                    <AdminCustomerCosts />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/ai-usage"
                element={
                  <PrivateRoute adminOnly>
                    <AIUsageAdmin />
                  </PrivateRoute>
                }
              />

              {/* Brand Kit — logo, colours and CTA for decks + clip pages.
                  Deliberately NOT requirePaidPlan: the page reads the tier
                  from the server and shows locked fields with the plan that
                  unlocks them, which is more useful than a redirect. */}
              <Route
                path="/settings/brand"
                element={
                  <PrivateRoute>
                    <BrandKit />
                  </PrivateRoute>
                }
              />

              {/* Custom Domains route */}
              <Route
                path="/settings/domains"
                element={
                  <PrivateRoute>
                    <CustomDomains />
                  </PrivateRoute>
                }
              />

              {/* Enterprise API & Webhooks */}
              <Route
                path="/enterprise/api-webhooks"
                element={
                  <PrivateRoute>
                    <APIWebhooks />
                  </PrivateRoute>
                }
              />

              {/* AI Tools routes — paid-plan gated (Grow hub / conversion
                  features). Free users hit the upgrade prompt instead of
                  the feature page. Trial users pass. */}
              <Route
                path="/ai/clips"
                element={
                  <PrivateRoute requirePaidPlan feature="AI tools">
                    <AIClipGenerator />
                  </PrivateRoute>
                }
              />
              <Route
                path="/my-clips"
                element={
                  <PrivateRoute>
                    <MyClips />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/connected-accounts"
                element={
                  <PrivateRoute>
                    <ConnectedAccounts />
                  </PrivateRoute>
                }
              />
              {/* AI assistants the host let into their account (OAuth). Open without
                  a plan so a lapsed host can still disconnect an app; the server
                  already limits a lapsed host's apps to reading. */}
              <Route
                path="/settings/connected-apps"
                element={
                  <PrivateRoute requirePlan={false}>
                    <ConnectedApps />
                  </PrivateRoute>
                }
              />
              {/* OAuth consent — an assistant sends the host here to approve it.
                  Signed-in only (PrivateRoute returns here after login with the
                  query intact); no plan wall, same reason as above. */}
              <Route
                path="/oauth/authorize"
                element={
                  <PrivateRoute requirePlan={false}>
                    <OAuthAuthorize />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings/payments"
                element={
                  <PrivateRoute>
                    <PaymentProcessors />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ai/script"
                element={
                  <PrivateRoute requirePaidPlan feature="Showrunner">
                    <TierNotice feature="Showrunner"><AIScriptGenerator /></TierNotice>
                  </PrivateRoute>
                }
              />
              <Route
                path="/decks"
                element={
                  <PrivateRoute requirePaidPlan feature="Showrunner">
                    <TierNotice feature="Slide Decks"><SlideDecks /></TierNotice>
                  </PrivateRoute>
                }
              />
              <Route
                path="/decks/:deckId"
                element={
                  <PrivateRoute requirePaidPlan feature="Showrunner">
                    <SlideDeckEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ai/thumbnail"
                element={
                  <PrivateRoute requirePaidPlan feature="AI tools">
                    <AIThumbnailGenerator />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ai/agents/:webinarId"
                element={
                  <PrivateRoute requireSubscription feature="AI Backstage Crew">
                    <FeatureGate featureKey="ai_agents" feature="AI Backstage Crew">
                      <AIAgentSettings />
                    </FeatureGate>
                  </PrivateRoute>
                }
              />
              <Route
                path="/ai/agents/:webinarId/report"
                element={
                  <PrivateRoute requireSubscription feature="AI Backstage Crew">
                    <FeatureGate featureKey="ai_agents" feature="AI Backstage Crew">
                      <AIAgentReport />
                    </FeatureGate>
                  </PrivateRoute>
                }
              />

              {/* Page Builder routes */}
              <Route
                path="/page-builder"
                element={
                  <PrivateRoute>
                    <PageBuilderPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/page-builder/:webinarId"
                element={
                  <PrivateRoute>
                    <PageBuilderPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/page-builder/:webinarId/:templateId"
                element={
                  <PrivateRoute>
                    <PageBuilderPage />
                  </PrivateRoute>
                }
              />

              {/* Broadcasts Dashboard route */}
              <Route
                path="/dashboard/broadcasts"
                element={
                  <PrivateRoute>
                    <BroadcastsDashboard />
                  </PrivateRoute>
                }
              />


              {/* Template Chooser route */}
              <Route
                path="/templates"
                element={
                  <PrivateRoute>
                    <TemplateChooser />
                  </PrivateRoute>
                }
              />

              {/* Template Preview route (public for previews) */}
              <Route path="/template-preview/:templateId" element={<TemplatePreview />} />

              {/* The page list lives at /marketing/landing-pages (plan-gated);
                  this older address opened it without the gate. */}
              <Route path="/template-library" element={<Navigate to="/marketing/landing-pages" replace />} />

              {/* Marketing / Grow routes — all paid-plan gated. Free
                  users land on the upgrade prompt instead of the feature
                  page. Trial users pass. */}
              <Route
                path="/marketing/promo-hub"
                element={
                  <PrivateRoute requirePaidPlan feature="Promo Hub">
                    <TierNotice feature="Promo Hub"><PromoHubPage /></TierNotice>
                  </PrivateRoute>
                }
              />
              <Route
                path="/marketing/landing-pages"
                element={
                  <PrivateRoute requirePaidPlan feature="landing pages">
                    <TemplateLibrary />
                  </PrivateRoute>
                }
              />
              {/* A/B testing is hidden until the public page serves variants
                  (it never did). The old address goes to the page list. */}
              <Route path="/marketing/ab-testing" element={<Navigate to="/marketing/landing-pages" replace />} />
              <Route
                path="/marketing/qr-codes"
                element={
                  <PrivateRoute requirePaidPlan feature="QR codes">
                    <TierNotice feature="QR Codes"><QRCodes /></TierNotice>
                  </PrivateRoute>
                }
              />

              {/* Video Library route */}
              <Route
                path="/dashboard/videos"
                element={
                  <PrivateRoute>
                    <VideoLibrary />
                  </PrivateRoute>
                }
              />

              {/* Media Library route */}
              <Route
                path="/dashboard/media"
                element={
                  // A former customer can still reach their recordings to
                  // download them: the removal email links here, and their
                  // recordings are kept 60 days after the plan ends.
                  <PrivateRoute allowLapsed>
                    <MediaLibrary />
                  </PrivateRoute>
                }
              />

              {/* Webinar Hub route */}
              <Route
                path="/webinar/:webinarId/hub"
                element={
                  <PrivateRoute allowLapsed>
                    <WebinarHub />
                  </PrivateRoute>
                }
              />

              {/* Evergreen routes */}
              {/* The old Automated Sessions page: dashboard home lists the same
                  sessions (Automated filter) with their real state. */}
              <Route
                path="/dashboard/evergreen"
                element={<Navigate to="/dashboard?filter=automated" replace />}
              />
              <Route
                path="/webinars/:webinarId/evergreen"
                element={
                  <PrivateRoute>
                    <EvergreenSettingsPage />
                  </PrivateRoute>
                }
              />

              {/* Team invitation acceptance (works for logged-in and anonymous users) */}
              <Route path="/team/invite/:token" element={<TeamInvitePage />} />

              {/* Reusable join code landing (join an org as a viewer) */}
              <Route path="/join-org/:code" element={<JoinByCode />} />

              {/* Guest routes (public - guests join via invite link) */}
              <Route path="/join/:sessionId" element={<GuestJoin />} />
              {/* Co-host invitation link (emailed). Public: the page itself asks the invitee to sign in. */}
              <Route path="/webinar/invite/accept" element={<CoHostInviteAccept />} />
              <Route path="/follow/w/:webinarId" element={<FollowHost />} />
              <Route path="/backstage/:sessionId" element={<Backstage />} />
              <Route path="/stage/:sessionId" element={<Stage />} />

              {/* Evergreen webinar watch page (public - attendees watch via session link) */}
              <Route path="/webinar/:webinarId/watch" element={<WebinarWatch />} />

              {/* Live webinar watch page (public - attendees watch live broadcast) */}
              <Route path="/webinar/:webinarId/live" element={<LiveWebinarWatch />} />

              {/* Replay page (public - attendees watch a finished live webinar's recording) */}
              <Route path="/webinar/:webinarId/replay" element={<WebinarReplay />} />

              {/* "Add to Calendar" from the registration confirmation email */}
              <Route path="/webinar/:webinarId/calendar" element={<AddToCalendar />} />

              {/* Embeddable stream player (public - for external websites) */}
              <Route path="/embed/:webinarId" element={<EmbedPlayer />} />

              {/* Public webinar registration page */}
              <Route path="/webinars/:id/register" element={<LegacyRegisterRedirect />} />

              {/* Public webinar landing pages */}
              <Route path="/w/:slug" element={<PublicWebinarPage />} />

              {/* Cohort 1 enrollment — the founder-pricing landing page
                  for the inaugural 4-A Method Intensive. Public; no
                  auth. Hand-tuned page (not Page Builder) so the
                  methodology launch has a version-controlled surface. */}
              <Route path="/cohort-1" element={<Cohort1Enrollment />} />

              {/* Public webinar registration page (slug-based) */}
              <Route path="/register/:slug" element={<WebinarRegisterSlug />} />
              {/* Public branded clip landing page (shareable) */}
              <Route path="/clip/:uid" element={<ClipLanding />} />
              {/* Public branded watch page for a whole recording (video message) */}
              <Route path="/watch/:id" element={<ClipLanding source="watch" />} />

              {/* Paid-webinar checkout success — Stripe redirects buyers
                  here after Connect Checkout. Polls /purchase-status
                  until the webhook flips the Registration to paid. */}
              <Route path="/webinar/:id/purchase-success" element={<WebinarPurchaseSuccess />} />

              {/* Podcast routes — open to any signed-in user; shows come with paid
                  plans (Build 2, Serve 3, Studio and above unlimited). The server
                  enforces the limit, and the dashboard explains it. */}
              <Route
                path="/podcasts"
                element={
                  <PrivateRoute>
                    <PodcastsDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/podcasts/create"
                element={
                  <PrivateRoute>
                    <PodcastCreate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/podcasts/:podcastId"
                element={
                  <PrivateRoute>
                    <PodcastDetail />
                  </PrivateRoute>
                }
              />
              <Route
                path="/podcasts/:podcastId/episodes/create"
                element={
                  <PrivateRoute>
                    <EpisodeCreate />
                  </PrivateRoute>
                }
              />
              <Route
                path="/podcasts/:podcastId/episodes/:episodeId/edit"
                element={
                  <PrivateRoute>
                    <EpisodeEdit />
                  </PrivateRoute>
                }
              />
              <Route
                path="/podcasts/:podcastId/episodes/:episodeId/audio-editor"
                element={
                  <PrivateRoute>
                    <EpisodeAudioEditor />
                  </PrivateRoute>
                }
              />
              <Route
                path="/podcasts/:podcastId/settings"
                element={
                  <PrivateRoute>
                    <PodcastSettings />
                  </PrivateRoute>
                }
              />

              {/* Public podcast pages (no auth) */}
              <Route path="/podcasts/browse" element={<PodcastBrowse />} />
              <Route path="/podcast/:slug" element={<PodcastPublicPage />} />

              {/* Creator Courses — paid-plan gated (Professional+ for creation
                  and management). Public catalog / learning pages below are
                  not gated so enrolled students on any plan can still watch. */}
              <Route
                path="/courses"
                element={
                  <PrivateRoute requirePaidPlan feature="course builder">
                    <CoursesDashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/courses/create"
                element={
                  <PrivateRoute requirePaidPlan feature="course builder">
                    <CourseCreate />
                  </PrivateRoute>
                }
              />
              {/* Course wizard: plan a course with AI (from scratch, or from a
                  Showrunner session via router state). */}
              <Route
                path="/courses/new/ai"
                element={
                  <PrivateRoute requirePaidPlan feature="course builder">
                    <CourseWizardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/courses/:courseId"
                element={<CourseBuilderRoute />}
              />
              {/* Public course pages */}
              <Route path="/course/catalog" element={<CourseCatalog />} />
              <Route path="/course/:courseId" element={<CourseCatalog />} />
              <Route path="/course/:courseId/learn" element={<CourseLearning />} />
              <Route path="/creator/:userId/courses" element={<CreatorCoursePage />} />
              {/* Course certificates — the certificate page and verify are public
                  (verify shows names + dates only); the list needs sign-in. */}
              <Route path="/certificates/:number" element={<CertificatePage />} />
              <Route path="/verify/:number" element={<VerifyCertificate />} />
              <Route
                path="/certificates"
                element={
                  <PrivateRoute>
                    <MyCertificates />
                  </PrivateRoute>
                }
              />

              {/* Maintenance mode route */}
              <Route path="/maintenance" element={<Maintenance />} />
              {/* Public system status (no sign-in) — also at /dashboard/status */}
              <Route path="/status" element={<SystemStatus />} />

              {/* 404 catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
        {!isEmbedded && <Footer />}
        {/* AI Support Chat Widget - available on all pages (hidden in the native Studio WebView) */}
        {!isEmbedded && <AISupportWidget />}
        {/* Admin jump-to box (Ctrl/Cmd+K) + admin shortcuts; admin pages only. */}
        {!isEmbedded && <AdminCommandPalette />}
        {/* Connection status indicator - minimal dot on global pages, auto-dismisses errors */}
        <ConnectionStatus minimal context="global" />
        {/* Site-wide maintenance warning. Rendered even when embedded: the
            native Studio WebView is exactly where a host is mid-broadcast and
            most needs to know a restart is coming. */}
        <MaintenanceBanner />
        {/* "Your captions are ready" on any page, when a long job finishes. */}
        {!isEmbedded && <JobDoneNotifier />}
        {/* "We'll be right back" when the server can't be reached. */}
        <ServerOutageGuard />
        {/* GDPR Cookie Consent Banner */}
        {!isEmbedded && <CookieConsent />}
      </div>
      </TrialSignupProvider>
    </Router>
  );
}

function App() {
  return (
    <SEOProvider>
      <ThemeProvider>
        <ToastProvider>
          <ApiErrorNotifier />
          <AuthProvider>
            <TeamProvider>
              <PaymentProvider>
                <FeatureFlagsProvider>
                  <SocketProvider>
                    <SidebarProvider>
                      <AppContent />
                    </SidebarProvider>
                  </SocketProvider>
                </FeatureFlagsProvider>
              </PaymentProvider>
            </TeamProvider>
          </AuthProvider>
        </ToastProvider>
      </ThemeProvider>
    </SEOProvider>
  );
}

export default App;
